<template>
    <div
        class="hero-ui-sequence"
        :class="{
            'hero-ui-sequence--play': isPlaying,
            'hero-ui-sequence--paused': isPaused,
            'hero-ui-sequence--fade-out': isFadingOut,
        }"
        aria-hidden="true"
    >
        <div v-if="prompt" class="hero-ui-sequence__search">
            <div class="hero-ui-sequence__row">
                <div class="hero-ui-sequence__field">
                    <span v-if="prompt.firstLine" class="hero-ui-sequence__line-wrapper">
                        <span class="hero-ui-sequence__line hero-ui-sequence__line--first">{{ prompt.firstLine }}</span>
                        <span class="hero-ui-sequence__caret hero-ui-sequence__caret--first" />
                    </span>
                    <span v-if="prompt.secondLine" class="hero-ui-sequence__line-wrapper">
                        <span class="hero-ui-sequence__line hero-ui-sequence__line--second">{{ prompt.secondLine }}</span>
                        <span class="hero-ui-sequence__caret hero-ui-sequence__caret--second" />
                    </span>
                </div>
                <div v-if="navbarMode === 'client'" :class="['hero-ui-sequence__button', 'hero-ui-sequence__button--client']">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
                        <circle cx="10.5" cy="10.5" r="7" />
                        <path d="M16 16l5 5" />
                    </svg>
                </div>
                <div v-else class="hero-ui-sequence__button">
                    <VJoyButton variant="main" size="xsmall" :label="$t('general.send')" />
                </div>
            </div>
        </div>

        <div class="hero-ui-sequence__cards">
            <template v-for="(card, index) in cards" :key="index">
                <div v-if="card.name" class="hero-ui-sequence__card" :class="`hero-ui-sequence__card--${index + 1}`">
                    <HeroProfileCard v-bind="card" />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useState} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import HeroProfileCard from './HeroProfileCard.vue';
    import {HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS} from '../composables/useHeroSequence';

    interface SequencePrompt {
        firstLine: string;
        secondLine: string;
    }

    interface SequenceCard {
        jobTitle: string;
        company?: string;
        name: string;
        photoPosition: string;
        photoUrl: string;
        photoAlt: string;
        review: string;
        score: string;
    }

    interface Props {
        isPlaying: boolean;
        isPaused?: boolean;
        isFadingOut?: boolean;
        prompt: SequencePrompt | null;
        cards: SequenceCard[];
    }

    const {prompt} = defineProps<Props>();

    const navbarMode = useState('navbar-unlogged-selected-mode');

    const fadeOutDuration = `${HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS}s`;
    const firstLineSteps = computed(() => prompt?.firstLine.length ?? 0);
    const secondLineSteps = computed(() => prompt?.secondLine.length ?? 0);
</script>

<style lang="scss" scoped>
    $ease-out: cubic-bezier(0.22, 0.61, 0.36, 1);

    .hero-ui-sequence {
        display: none;

        @media screen and (min-width: 1280px) {
            display: block;
            position: absolute;
            z-index: 4;
            inset: 0;
            color: var(--joy-color-neutral-60);
            pointer-events: none;
            transition: opacity v-bind(fadeOutDuration) $ease-out;
        }

        // Leaves with the video just before the loop restarts, the next iteration replays the whole composition.
        &--fade-out {
            opacity: 0;
        }

        --hero-ui-sequence-play-state: running;

        &--paused {
            --hero-ui-sequence-play-state: paused;
        }

        &__search {
            position: absolute;
            top: 120px;
            width: 450px;
            right: 80px;
            padding: var(--joy-core-spacing-2);
            border: 1px solid rgb(255 255 255 / 70%);
            border-radius: 22px;
            opacity: 0;
            background-color: color-mix(in srgb, var(--joy-color-neutral-0) 92%, transparent);
            backdrop-filter: blur(20px);
            box-shadow:
                0 32px 80px rgb(20 8 8 / 34%),
                inset 0 2px 0 rgb(255 255 255 / 60%);

            @media screen and (min-width: 1400px) {
                width: 550px;
            }

            @media screen and (min-width: 1450px) {
                width: 600px;
                padding: var(--joy-core-spacing-4);
            }
        }

        &__row {
            display: flex;
            align-items: flex-end;
            gap: var(--joy-core-spacing-4);
        }

        &__field {
            min-width: 0;
            flex: 1;
            padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);
            border-radius: var(--joy-core-radius-4);
            background-color: var(--joy-color-neutral-20);
        }

        &__line {
            display: block;
            width: max-content;
            min-width: 0;
            max-width: 0;
            overflow: hidden;
            overflow-wrap: break-word;
            font-size: var(--joy-font-size-primary-400);
            font-weight: 400;
            line-height: var(--joy-line-height-large);
            white-space: nowrap;
        }

        &__line-wrapper {
            display: flex;
            align-items: center;
            max-width: 100%;
            min-width: 0;
        }

        &__caret {
            display: block;
            flex: none;
            width: 2px;
            height: 18px;
            opacity: 0;
            background-color: var(--joy-color-primary-50);
        }

        &__button {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--joy-color-primary-50);
            color: var(--joy-color-neutral-0);
            box-shadow: 0 12px 28px rgb(252 87 87 / 45%);
            border-radius: var(--joy-core-radius-4);

            &--client {
                width: 40px;
                height: 40px;
                border-radius: 50%;

                svg {
                    width: 20px;
                }

                @media screen and (min-width: 1450px) {
                    width: 50px;
                    height: 50px;
                }
            }
        }

        &__cards {
            position: absolute;
            top: 50%;
            right: 90px;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            transform: translateY(-50%);
            gap: var(--joy-core-spacing-2);
        }

        &__card {
            opacity: 0;

            &--2 {
                margin-right: 50px;
            }
        }

        // Every delay below is counted from HERO_UI_SEQUENCE_START_IN_SECONDS, where this class is added, and
        // the choreography has to be over before the fade-out starts at 5.74s.
        &--play {
            .hero-ui-sequence__search {
                // `forwards` and not `both` on the exit, otherwise the panel would show before its entrance.
                animation:
                    hero-ui-sequence-pop 0.45s $ease-out both,
                    hero-ui-sequence-out 0.35s $ease-out 2.15s forwards;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__line--first {
                animation:
                    hero-ui-sequence-type-in 0.7s steps(v-bind(firstLineSteps), end) 0.4s both,
                    hero-ui-sequence-wrap 0.01s linear 1.1s forwards;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__line--second {
                animation:
                    hero-ui-sequence-type-in 0.7s steps(v-bind(secondLineSteps), end) 1.1s both,
                    hero-ui-sequence-wrap-second 0.01s linear 1.8s forwards;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__caret--first {
                animation: hero-ui-sequence-caret 0.35s steps(1, end) 0.4s 2 both;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__caret--second {
                animation: hero-ui-sequence-caret 0.35s steps(1, end) 1.1s 2 forwards;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__button {
                animation:
                    hero-ui-sequence-press 0.5s cubic-bezier(0.3, 1.4, 0.5, 1) 2.1s both,
                    hero-ui-sequence-glow 0.8s ease-out 2.1s both;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__card--1 {
                animation: hero-ui-sequence-pop 0.45s $ease-out 2.35s both;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__card--2 {
                animation: hero-ui-sequence-pop 0.45s $ease-out 2.55s both;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }

            .hero-ui-sequence__card--3 {
                animation: hero-ui-sequence-pop 0.45s $ease-out 2.75s both;
                animation-play-state: var(--hero-ui-sequence-play-state);
            }
        }
    }

    @keyframes hero-ui-sequence-pop {
        0% {
            opacity: 0;
            transform: translateX(40px) scale(0.94);
        }

        60% {
            opacity: 1;
            transform: translateX(0) scale(1.02);
        }

        100% {
            opacity: 1;
            transform: translateX(0) scale(1);
        }
    }

    @keyframes hero-ui-sequence-out {
        to {
            opacity: 0;
            transform: translateX(24px) scale(0.97);
        }
    }

    @keyframes hero-ui-sequence-type-in {
        from {
            max-width: 0;
        }

        to {
            max-width: 100%;
        }
    }

    @keyframes hero-ui-sequence-wrap {
        to {
            overflow: visible;
            white-space: normal;
        }
    }

    @keyframes hero-ui-sequence-wrap-second {
        to {
            display: inline;
            overflow: visible;
            white-space: normal;
        }
    }

    @keyframes hero-ui-sequence-caret {
        0%,
        45% {
            opacity: 1;
        }

        50%,
        95% {
            opacity: 0;
        }

        100% {
            opacity: 0;
        }
    }

    @keyframes hero-ui-sequence-press {
        0% {
            transform: scale(1);
        }

        40% {
            transform: scale(0.9);
        }

        100% {
            transform: scale(1);
        }
    }

    @keyframes hero-ui-sequence-glow {
        0% {
            box-shadow: 0 0 0 0 rgb(252 87 87 / 55%);
        }

        100% {
            box-shadow: 0 0 0 28px rgb(252 87 87 / 0%);
        }
    }

    // The end state of the sequence is the three cards, the query panel has faded out by then.
    @media (prefers-reduced-motion: reduce) {
        .hero-ui-sequence {
            opacity: 1;
            transition: none;

            &__search {
                display: none;
            }

            &--play &__card {
                animation: none;
            }

            &__card {
                opacity: 1;
                transform: none;
            }
        }
    }
</style>
